import { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input } from '../atoms/Input';
import { PATHS } from '../commons/paths';
import { StyledLoginFormContainer } from 'templates/StyledLoginFormContainer';
import { useTranslationWithLang } from 'hooks/useTranslationWithLang';
import { BackButton } from 'atoms/BackButton';
import { useCreateResetPasswordSchema } from 'hooks/u007/useCreateResetPasswordSchema';
import { CreateResetPasswordType } from 'commons/passwordChange';
import { TransparentFooter } from 'templates/TransparentFooter';
import { StyledFormError } from 'organisms/u001/StyledFormError';
import { SubmitHandler, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import LoadingModal from 'organisms/LoadingModal';
import { useForgetPassword } from 'hooks/u007/userForgetPassword';
import { AppContext } from 'templates/AppContextProvider';

export function ResetPassword() {
  // hooks
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const { t: tCommons } = useTranslationWithLang('commons');
  const ResetPasswordSchema = useCreateResetPasswordSchema();
  const { currentLang } = useContext(AppContext);
  const {
    register,
    handleSubmit,
    formState: { errors },
    clearErrors,
  } = useForm<CreateResetPasswordType>({
    resolver: zodResolver(ResetPasswordSchema),
    shouldFocusError: true,
  });
  useEffect(() => {
    clearErrors();
  }, [currentLang, clearErrors]);
  const [forgetPassword] = useForgetPassword();
  // function
  const onClickChangePassword: SubmitHandler<CreateResetPasswordType> = useCallback(
    async (form) => {
      setLoading(true);
      setErrorMessage('');
      await forgetPassword({
        request: { username: form.id },
        onSuccess: () => {
          setLoading(false);
          navigate(PATHS.CONFIRM_RESET_PASSWORD);
        },
        onError: (err) => {
          setLoading(false);
          setErrorMessage(
            err.message === 'MU002F0128E' ? tCommons('TU002_112') : tCommons('TU000_058')
          );
        },
      });
    },
    [navigate, forgetPassword, tCommons]
  );

  return (
    <>
      {loading && <LoadingModal />}
      <StyledLoginFormContainer title={tCommons('TU000_015')}>
        <form
          className="w-full px-8 py-4 flex flex-col items-center justify-center"
          onSubmit={handleSubmit(onClickChangePassword)}
        >
          <div role="heading" aria-level={1} aria-live="assertive" className="sr-only">
            {tCommons('TU000_015')}
          </div>
          <Input
            type="text"
            className="w-[300px] my-6 px-5 rounded-full"
            {...register('id')}
            placeholder={tCommons('TU000_016')}
            autoComplete="username"
          />
          <StyledFormError errorMessage={errors.id?.message || errorMessage} />
          <div className="flex justify-center items-center">
            <BackButton className="mr-2" style={{ color: '#FFF', borderColor: '#FFF' }} />
            <button
              type="submit"
              className="text-[#d02a3be9] font-bold my-6 w-[12rem] bg-white hover:bg-opacity-80 py-4 rounded-full"
              title={tCommons('TU000_015')}
            >
              {tCommons('TU000_015')}
            </button>
          </div>
        </form>
        <TransparentFooter textColor={'#FFF'} />
      </StyledLoginFormContainer>
    </>
  );
}
